import {
  Box,
  Button,
  Modal as ChakraModal,
  Flex,
  FormLabel,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
  Select
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { IOurStudentFormFields, ITestimonialsFormModalProps } from '../../../interfaces/Testimonials/ITestimonials'
import { TestimonialsService } from '../../../services/Testimonials/TestimonialsService'

const baseForm: IOurStudentFormFields = {
  name: '',
  knowledge_area_id: 0,
  description: '',
  video_url: '',
  image: undefined
}

const baseFormErrors = {
  name: '',
  knowledge_area_id: '',
  description: '',
  video_url: '',
  image: ''
}

export function TestimonialsFormModal({
  isOpen,
  onClose,
  ourStudent,
  knowledgeAreas,
  onSuccess
}: ITestimonialsFormModalProps) {
  const service = new TestimonialsService()
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<IOurStudentFormFields>(baseForm)
  const [formErrors, setFormErrors] = useState(baseFormErrors)

  useEffect(() => {
    if (isOpen) {
      setSaving(false)
      setFormErrors(baseFormErrors)
      setForm(
        ourStudent
          ? {
              name: ourStudent.name,
              knowledge_area_id: ourStudent.knowledge_area_id,
              description: ourStudent.description,
              video_url: ourStudent.video_url,
              image: undefined
            }
          : baseForm
      )
    }
  }, [isOpen, ourStudent])

  const handleSave = async () => {
    setSaving(true)
    service
      .create(form)
      .then(() => {
        toast({
          title: 'Registro salvo com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        onClose()
        onSuccess?.()
      })
      .catch((response) => {
        if (response.response?.status === 422) {
          setFormErrors(response.response.data.errors)
          return
        }

        toast({
          title: 'Erro ao adicionar estudante',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{ourStudent ? 'Editar' : 'Registrar'} Estudante</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={3}>
            <Box>
              <FormLabel htmlFor="name">Nome</FormLabel>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={saving}
              />
              {formErrors.name && <Text color="red.500">{formErrors.name}</Text>}
            </Box>

            <Box>
              <FormLabel htmlFor="knowledge_area_id">Área de Conhecimento</FormLabel>
              <Select
                id="knowledge_area_id"
                value={form.knowledge_area_id}
                onChange={(e) => setForm({ ...form, knowledge_area_id: parseInt(e.target.value) })}
                disabled={saving}
              >
                <option value={0}>Selecione uma área</option>
                {knowledgeAreas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </Select>
              {formErrors.knowledge_area_id && <Text color="red.500">{formErrors.knowledge_area_id}</Text>}
            </Box>

            <Box>
              <FormLabel htmlFor="description">Depoimento</FormLabel>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                disabled={saving}
              />
              {formErrors.description && <Text color="red.500">{formErrors.description}</Text>}
            </Box>

            <Box>
              <FormLabel htmlFor="video_url">URL do Vídeo</FormLabel>
              <Input
                id="video_url"
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                disabled={saving}
              />
              {formErrors.video_url && <Text color="red.500">{formErrors.video_url}</Text>}
            </Box>

            <Box>
              <FormLabel htmlFor="image">Imagem</FormLabel>
              <Input
                id="image"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) setForm({ ...form, image: file })
                }}
                disabled={saving}
              />
              {formErrors.image && <Text color="red.500">{formErrors.image}</Text>}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="magenta" mr={3} onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  )
}
